import {
  Accordion,
  AccordionItem,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Link,
  Popover,
  PopoverTrigger,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { AccordionPanel } from '@gamma/ui/layout';
import CloseOutlinedIcon from '@gamma/ui/icons/CloseOutlinedIcon';
import DownloadSharpIcon from '@gamma/ui/icons/DownloadSharpIcon';
import InfoSharpIcon from '@gamma/ui/icons/InfoSharpIcon';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  DetailsGrid,
  DetailsGridRow,
} from '@investigator/features/detections/components';
import { SimpleCaretPagination } from '@investigator/shared/components';
import { useQueryWithRetry } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { SlideDrawerContext } from '@investigator/shared/providers';
import moment from 'moment';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { safeParse } from 'secure-json-parse';
import {
  ICreateQueryJob,
  IPollQueryJob,
  useCreateQueryJob,
  usePollQueryJob,
} from '../../../../../../../../../../../investigations/queries';
import { useHumioToken } from '@investigator/shared/providers';
import { InvestigateLogsTableModal } from '../InvestigateLogsTableModal';
import type { LogDetails } from '../InvestigateLogsTableModal/InvestigateLogsTableModal';
import { LogscaleDetailsModal } from '../LogscaleDetailsModal';
import { LogscaleMenuButton } from '../LogscaleMenuButton';
import { ConnHistorySummary } from './ConnHistorySummary';
import { buildSearchQueryString } from './utils/buildSearchQueryString';

const {
  connection,
  dns,
  http,
  ssl,
  file,
  smb,
  intel,
  notice,
  suricata,
  yara,
  anomaly,
  seeMore,
} = i18n.pages.detections.logscaleDetails;
const { controls } = i18n.pages.detections;

interface LogscaleDetailsProps {
  alertsUid: string;
  alertsName: string;
  alertsTimestamp: string;
  alertsTenant?: string;
  logScaleQuery?: string;
  childrenBelowHeader?: ReactNode;
  alertType?: string;
}

interface connectionDetailsParsedData {
  '#path': 'conn';
  history: string;
  orig_bytes: string;
  local_orig: string;
  local_resp: string;
  resp_bytes: string;
  service: string;
  _write_ts: string;
  'id.resp_p': string;
  'spcap.url': string;
}

interface httpDetailsParsedData {
  '#path': 'http';
  method: string;
  uri: string;
  user_agent: string;
  post_body: string;
}

interface sslDetailsParsedData {
  '#path': 'ssl';
  subject: string;
  '@rawstring': string;
  server_name: string;
  issuer: string;
  validation_status: string;
}

interface fileDetailsParsedData {
  '#path': 'files';
  mime_type: string;
  'rx_hosts[0]': string;
  seen_bytes: string;
  extracted?: string;
  sha1?: string;
}

interface dnsDetailsParsedData {
  '#path': 'dns';
  '@rawstring': string;
  answers: string[];
  query: string;
  qtype_name: string;
  rcode_name: string;
}

interface smbDetailsParsedData {
  '#path': 'smb_mapping';
  path: string;
}

interface intelDetailsParsedData {
  '#path': 'intel';
  'seen.indicator': string;
  'seen.indicator_type': string;
  'seen.where': string;
}

interface noticeDetailsParsedData {
  '#path': 'notice';
  msg: string;
  sub: string;
  note: string;
}

interface suricataDetailsParsedData {
  '#path': 'suricata_corelight';
  'alert.category': string;
  'alert.signature'?: string;
}

interface yaraDetailsParsedData {
  '#path': 'yara_corelight';
  'alert.signature': string;
}

interface anomalyDetailsParsedData {
  '#path': 'anomaly';
  usecase_description: string;
  original_entity: string;
  item: string;
  item_score: string;
  'alert.signature'?: string;
}

type LogDetail =
  | connectionDetailsParsedData
  | dnsDetailsParsedData
  | httpDetailsParsedData
  | fileDetailsParsedData
  | sslDetailsParsedData
  | smbDetailsParsedData
  | intelDetailsParsedData
  | noticeDetailsParsedData
  | suricataDetailsParsedData
  | yaraDetailsParsedData
  | anomalyDetailsParsedData;

const NoDataPanel = ({ title }: { title: string }) => {
  return (
    <AccordionItem bgColor="transparent">
      <Box px={4} py={2} color="text.secondary" fontSize={16}>
        {title} (N/A)
      </Box>
    </AccordionItem>
  );
};

export const LogscaleDetails = ({
  alertsUid,
  alertsName,
  alertsTimestamp,
  alertsTenant,
  logScaleQuery,
  childrenBelowHeader,
  alertType,
}: LogscaleDetailsProps) => {
  const { setIsSlideDrawerOpen, slideDrawerOnClose } =
    useContext(SlideDrawerContext);
  const { token: sessionToken } = useHumioToken();
  const [connectionDetailsParsedData, setConnectionDetailsParsedData] =
    useState<connectionDetailsParsedData>();
  const [httpDetailsParsedData, setHttpDetailsParsedData] = useState<
    httpDetailsParsedData[]
  >([]);
  const [sslDetailsParsedData, setSslDetailsParsedData] =
    useState<sslDetailsParsedData>();
  const [fileDetailsParsedData, setFileDetailsParsedData] = useState<
    fileDetailsParsedData[]
  >([]);
  const [dnsDetailsParsedData, setDnsDetailsParsedData] = useState<
    dnsDetailsParsedData[]
  >([]);
  const [smbDetailsParsedData, setSmbDetailsParsedData] =
    useState<smbDetailsParsedData>();
  const [intelDetailsParsedData, setIntelDetailsParsedData] = useState<
    intelDetailsParsedData[]
  >([]);
  const [noticeDetailsParsedData, setNoticeDetailsParsedData] = useState<
    noticeDetailsParsedData[]
  >([]);
  const [suricataDetailsParsedData, setSuricataDetailsParsedData] = useState<
    suricataDetailsParsedData[]
  >([]);
  const [yaraDetailsParsedData, setYaraDetailsParsedData] = useState<
    yaraDetailsParsedData[]
  >([]);
  const [anomalyDetailsParsedData, setAnomalyDetailsParsedData] = useState<
    anomalyDetailsParsedData[]
  >([]);
  // TODO: Fix any type usage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [moreDetailsParsedData, setMoreDetailsParsedData] = useState<any>();
  // TODO: Fix any type usage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [logDetailsParsedData, setLogDetailsParsedData] = useState<any[]>([]);

  const [logscaleDetailTitle, setLogscaleDetailTitle] = useState<string>();
  const [sslCertChainFPS, setSslCertChainFPS] = useState<string>('-');
  const [dnsCurrentPage, setDnsCurrentPage] = useState<number>(0);
  const [httpCurrentPage, setHttpCurrentPage] = useState<number>(0);
  const [filesCurrentPage, setFilesCurrentPage] = useState<number>(0);
  const [intelCurrentPage, setIntelCurrentPage] = useState<number>(0);
  const [noticeCurrentPage, setNoticeCurrentPage] = useState<number>(0);
  const [suricataCurrentPage, setSuricataCurrentPage] = useState<number>(0);
  const [yaraCurrentPage, setYaraCurrentPage] = useState<number>(0);
  const [anomalyCurrentPage, setAnomalyCurrentPage] = useState<number>(0);
  const [logDetailsQueryJobId, setLogDetailsQueryJobId] = useState<string>();
  const [downloadUrl, setLocalDownloadUrl] = useState<string>('');

  const {
    isOpen: showMoreConnectionIsOpen,
    onOpen: showMoreConnectionOnOpen,
    onClose: showMoreConnectionOnClose,
  } = useDisclosure();
  const {
    isOpen: investigateModalIsOpen,
    onOpen: investigateModalOnOpen,
    onClose: investigateModalOnClose,
  } = useDisclosure();

  const startTime = moment(alertsTimestamp).subtract(20, 'minutes').unix();
  const endTime = moment(alertsTimestamp).add(20, 'minutes').unix();

  const [
    logDetailsCreateQueryJob,
    { loading: logDetailsCreateQueryJobLoading },
  ] = useCreateQueryJob({
    variables: {
      clientSessionId: sessionToken || null,
      queryInputJob: {
        queryStringWithoutSortSize: alertsUid
          ? `uid="${alertsUid}"`
          : logScaleQuery,
        start: startTime.toString(),
        end: endTime.toString(),
        tenantsForQuery: [alertsTenant],
      },
      size: 400,
      sort: [
        {
          sort_by: '_write_ts',
          sort_dir: 'asc',
        },
      ],
    },
    onCompleted: (data: ICreateQueryJob) => {
      if (data?.createQueryJob?.query_job_id) {
        setLogDetailsQueryJobId(data.createQueryJob.query_job_id);
      }
    },
  });

  const { loading: logDetailsLoading } = useQueryWithRetry<
    IPollQueryJob,
    { clientSessionId: string | null; queryJobId: string | undefined }
  >({
    queryHook: usePollQueryJob,
    variables: {
      clientSessionId: sessionToken || null,
      queryJobId: logDetailsQueryJobId,
    },
    skip: !logDetailsQueryJobId,
    onCompleted(data) {
      const logsData = data?.pollQueryJob?.events?.map((item: string) =>
        safeParse(item),
      );
      setLogDetailsParsedData(logsData);
      if (data?.pollQueryJob.done) {
        setLogDetailsQueryJobId(undefined); // Job is completed so set to undefined to prevent future queries.
      }
    },
    onError(error) {
      console.error(error);
      setLogDetailsParsedData([]);
    },
  });

  const handleSearchQuery = (pathName: string, currentIndex: number) => {
    switch (pathName) {
      case 'conn': {
        setMoreDetailsParsedData(connectionDetailsParsedData);
        break;
      }
      case 'dns': {
        setMoreDetailsParsedData(dnsDetailsParsedData[currentIndex]);
        break;
      }
      case 'http': {
        setMoreDetailsParsedData(httpDetailsParsedData[currentIndex]);
        break;
      }
      case 'files': {
        setMoreDetailsParsedData(fileDetailsParsedData[currentIndex]);
        break;
      }
      case 'ssl': {
        setMoreDetailsParsedData(sslDetailsParsedData);
        break;
      }
      case 'smb_mapping': {
        setMoreDetailsParsedData(smbDetailsParsedData);
        break;
      }
      case 'intel': {
        setMoreDetailsParsedData(intelDetailsParsedData[currentIndex]);
        break;
      }
      case 'notice': {
        setMoreDetailsParsedData(noticeDetailsParsedData[currentIndex]);
        break;
      }
      case 'suricata_corelight': {
        setMoreDetailsParsedData(suricataDetailsParsedData[currentIndex]);
        break;
      }
      case 'yara_corelight': {
        setMoreDetailsParsedData(yaraDetailsParsedData[currentIndex]);
        break;
      }
      case 'anomaly': {
        setMoreDetailsParsedData(anomalyDetailsParsedData[currentIndex]);
        break;
      }
    }
  };

  const lastRequestKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!(alertsUid || logScaleQuery) || !(alertsTenant?.trim() ?? '')) return;
    const requestKey = `${alertsTenant ?? ''}-${alertsUid ?? ''}-${logScaleQuery ?? ''}-${startTime}-${endTime}`;
    if (lastRequestKeyRef.current === requestKey) {
      return;
    }
    lastRequestKeyRef.current = requestKey;
    logDetailsCreateQueryJob();
  }, [
    alertsUid,
    logScaleQuery,
    alertsTenant,
    startTime,
    endTime,
    logDetailsCreateQueryJob,
  ]);

  useEffect(() => {
    if (logDetailsParsedData.length > 0) {
      let connDetails: connectionDetailsParsedData;
      const dnsDetails: dnsDetailsParsedData[] = [];
      const httpDetails: httpDetailsParsedData[] = [];
      const filesDetails: fileDetailsParsedData[] = [];
      let sslDetails: sslDetailsParsedData;
      let smbDetails: smbDetailsParsedData;
      const intelDetails: intelDetailsParsedData[] = [];
      const noticeDetails: noticeDetailsParsedData[] = [];
      const suricataDetails: suricataDetailsParsedData[] = [];
      const yaraDetails: yaraDetailsParsedData[] = [];
      const anomalyDetails: anomalyDetailsParsedData[] = [];

      logDetailsParsedData.forEach((logDetail: LogDetail) => {
        switch (logDetail['#path']) {
          case 'conn': {
            connDetails = logDetail;
            const url = connDetails?.['spcap.url']
              ? connDetails?.['spcap.url']
              : '';
            setLocalDownloadUrl(url);
            setConnectionDetailsParsedData(connDetails);
            break;
          }
          case 'dns': {
            const rawString = safeParse(logDetail['@rawstring']);
            const answers = rawString?.answers as string[];
            const dnsDetail = { ...logDetail, answers };
            dnsDetails.push(dnsDetail);
            break;
          }
          case 'http': {
            httpDetails.push(logDetail);
            break;
          }
          case 'files': {
            filesDetails.push(logDetail);
            break;
          }
          case 'ssl': {
            sslDetails = logDetail;
            if (sslDetails['@rawstring']) {
              const rawString = safeParse(sslDetails['@rawstring']);
              const certChainFps = rawString?.cert_chain_fps as string[];
              setSslCertChainFPS(JSON.stringify(certChainFps));
            }
            setSslDetailsParsedData(sslDetails);
            break;
          }
          case 'smb_mapping': {
            smbDetails = logDetail;
            setSmbDetailsParsedData(smbDetails);
            break;
          }
          case 'intel': {
            intelDetails.push(logDetail);
            break;
          }
          case 'notice': {
            if (logDetail.note !== alertsName) {
              noticeDetails.push(logDetail);
            }
            break;
          }
          case 'suricata_corelight': {
            if (logDetail['alert.signature'] !== alertsName) {
              suricataDetails.push(logDetail);
            }
            break;
          }
          case 'yara_corelight': {
            if (logDetail['alert.signature'] !== alertsName) {
              yaraDetails.push(logDetail);
            }
            break;
          }
          case 'anomaly': {
            if (logDetail['alert.signature'] !== alertsName) {
              anomalyDetails.push(logDetail);
            }
            break;
          }
        }
      });

      setDnsDetailsParsedData(dnsDetails);
      setHttpDetailsParsedData(httpDetails);
      setFileDetailsParsedData(filesDetails);
      setIntelDetailsParsedData(intelDetails);
      setNoticeDetailsParsedData(noticeDetails);
      setSuricataDetailsParsedData(suricataDetails);
      setYaraDetailsParsedData(yaraDetails);
      setAnomalyDetailsParsedData(anomalyDetails);
    }
  }, [alertsName, logDetailsParsedData]);

  return (
    <VStack w="100%" alignItems="stretch" position="relative">
      <CloseButton
        onClick={() => {
          slideDrawerOnClose?.();
          setIsSlideDrawerOpen?.(false);
        }}
        size="md"
        aria-label="Close drawer"
        position="absolute"
        top={2}
        right={2}
        zIndex="docked"
      >
        <CloseOutlinedIcon />
      </CloseButton>
      <Box w="100%" pl={4} pr={8} py={3}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          wrap="wrap"
        >
          <Text
            textStyle="h5"
            wordBreak="break-word"
            overflowWrap="anywhere"
            flex="1 1 auto"
            minW={0}
          >
            {`${alertsName} | ${moment(alertsTimestamp).format()}`}
          </Text>
          <HStack>
            <LogscaleMenuButton
              isLoading={logDetailsLoading}
              searchQueryString={buildSearchQueryString({
                item: {
                  uid: alertsUid,
                  alert_info: {
                    alert_name: alertsName,
                    alert_type: '',
                  },
                  alert_entity: {
                    entity_name: '',
                    entity_type: '',
                  },
                  alert_timestamp: { observed: moment(alertsTimestamp).unix() },
                  alert_name: '',
                  severity: 0,
                  is_custom_severity: false,
                  first_seen: 0,
                  last_seen: 0,
                },
                logScaleQuery,
              })}
              investigateModalOnOpen={investigateModalOnOpen}
              showContent={true}
            />
            {downloadUrl && (
              <Button
                variant="solid"
                colorScheme="gray"
                as="a"
                target="_blank"
                href={downloadUrl}
                download="PCAP"
                aria-label={controls.downloadPcap}
                leftIcon={<DownloadSharpIcon />}
              >
                {controls.downloadPcap}
                <Popover trigger="hover" placement="top">
                  <PopoverTrigger>
                    <Tooltip
                      label={controls.downloadPcapTooltipText}
                      shouldWrapChildren
                    >
                      <InfoSharpIcon color="text.primary" pl={1} />
                    </Tooltip>
                  </PopoverTrigger>
                </Popover>
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>
      {childrenBelowHeader}
      <Accordion w="100%" reduceMotion>
        <Box w="100%">
          {!logDetailsCreateQueryJobLoading &&
            !logDetailsLoading &&
            !connectionDetailsParsedData && (
              <NoDataPanel title={connection.title} />
            )}
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            connectionDetailsParsedData) && (
            <AccordionPanel title={connection.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <DetailsGrid>
                  <DetailsGridRow
                    title={connection.history}
                    data-testid="detections-history"
                  >
                    {connectionDetailsParsedData?.history ? (
                      <ConnHistorySummary
                        history={connectionDetailsParsedData.history}
                      />
                    ) : (
                      <Text overflowWrap="anywhere">-</Text>
                    )}
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={connection.protocol}
                    data-testid="detections-protocol"
                  >
                    <Text overflowWrap="anywhere">
                      {connectionDetailsParsedData?.service
                        ? connectionDetailsParsedData.service
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={connection.port}
                    data-testid="detections-port"
                  >
                    <Text overflowWrap="anywhere">
                      {connectionDetailsParsedData?.['id.resp_p']
                        ? connectionDetailsParsedData['id.resp_p']
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={connection.bytesSent}
                    data-testid="detections-bytes-sent"
                  >
                    <Text overflowWrap="anywhere">
                      {connectionDetailsParsedData?.orig_bytes
                        ? connectionDetailsParsedData.orig_bytes
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={connection.bytesReturned}
                    data-testid="detections-bytes-returned"
                  >
                    <Text overflowWrap="anywhere">
                      {connectionDetailsParsedData?.resp_bytes
                        ? connectionDetailsParsedData.resp_bytes
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={connection.localOriginator}
                    data-testid="detections-local-originator"
                  >
                    <Text overflowWrap="anywhere">
                      {connectionDetailsParsedData?.local_orig
                        ? connectionDetailsParsedData.local_orig
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={connection.localResponder}
                    data-testid="detections-local-responder"
                  >
                    <Text>
                      {connectionDetailsParsedData?.local_resp
                        ? connectionDetailsParsedData.local_resp
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <Link
                    color="text.link"
                    data-testid="see-more-button"
                    onClick={() => {
                      handleSearchQuery('conn', 0);
                      setLogscaleDetailTitle(connection.title);
                      showMoreConnectionOnOpen();
                    }}
                  >
                    {seeMore}
                  </Link>
                </DetailsGrid>
              )}
            </AccordionPanel>
          )}
        </Box>

        <Box w="100%">
          {!logDetailsCreateQueryJobLoading &&
            !logDetailsLoading &&
            dnsDetailsParsedData.length === 0 && (
              <NoDataPanel title={dns.title} />
            )}
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            dnsDetailsParsedData.length > 0) && (
            <AccordionPanel title={dns.title} reduceMotion>
              {logDetailsLoading || logDetailsCreateQueryJobLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsLoading || logDetailsCreateQueryJobLoading}
                />
              ) : (
                <>
                  <SimpleCaretPagination
                    currentPage={dnsCurrentPage}
                    pageLength={dnsDetailsParsedData.length}
                    setCurrentPage={setDnsCurrentPage}
                    containerProps={{ mb: 4 }}
                  />
                  <DetailsGrid>
                    <DetailsGridRow
                      title={dns.answers}
                      data-testid="dns-answers"
                    >
                      <Text overflowWrap="anywhere">
                        {dnsDetailsParsedData[dnsCurrentPage]?.answers
                          ? dnsDetailsParsedData[dnsCurrentPage].answers
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow title={dns.query} data-testid="dns-query">
                      <Text overflowWrap="anywhere">
                        {dnsDetailsParsedData[dnsCurrentPage]?.query
                          ? dnsDetailsParsedData[dnsCurrentPage].query
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={dns.qtypeName}
                      data-testid="dns-qtype-name"
                    >
                      <Text overflowWrap="anywhere">
                        {dnsDetailsParsedData[dnsCurrentPage]?.qtype_name
                          ? dnsDetailsParsedData[dnsCurrentPage].qtype_name
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={dns.rcodeName}
                      data-testid="dns-rcode-name"
                    >
                      <Text overflowWrap="anywhere">
                        {dnsDetailsParsedData[dnsCurrentPage]?.rcode_name
                          ? dnsDetailsParsedData[dnsCurrentPage].rcode_name
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <Link
                      color="text.link"
                      data-testid="see-more-button"
                      onClick={() => {
                        handleSearchQuery('dns', dnsCurrentPage);
                        setLogscaleDetailTitle(dns.title);
                        showMoreConnectionOnOpen();
                      }}
                    >
                      {seeMore}
                    </Link>
                  </DetailsGrid>
                </>
              )}
            </AccordionPanel>
          )}
        </Box>

        <Box w="100%">
          {!logDetailsCreateQueryJobLoading &&
            !logDetailsLoading &&
            httpDetailsParsedData.length === 0 && (
              <NoDataPanel title={http.title} />
            )}
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            httpDetailsParsedData.length > 0) && (
            <AccordionPanel title={http.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <>
                  <SimpleCaretPagination
                    currentPage={httpCurrentPage}
                    pageLength={httpDetailsParsedData.length}
                    setCurrentPage={setHttpCurrentPage}
                    containerProps={{ mb: 4 }}
                  />
                  <DetailsGrid>
                    <DetailsGridRow
                      title={http.uri}
                      data-testid="detections-uri"
                    >
                      <Text overflowWrap="anywhere">
                        {httpDetailsParsedData[httpCurrentPage]?.uri
                          ? httpDetailsParsedData[httpCurrentPage]?.uri
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={http.method}
                      data-testid="detections-method"
                    >
                      <Text overflowWrap="anywhere">
                        {httpDetailsParsedData[httpCurrentPage]?.method
                          ? httpDetailsParsedData[httpCurrentPage]?.method
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={http.postBody}
                      data-testid="detections-post-body"
                    >
                      <Text overflowWrap="anywhere">
                        {httpDetailsParsedData[httpCurrentPage]?.post_body
                          ? httpDetailsParsedData[httpCurrentPage]?.post_body
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={http.userAgent}
                      data-testid="detections-user-agent"
                    >
                      <Text overflowWrap="anywhere">
                        {httpDetailsParsedData[httpCurrentPage]?.user_agent
                          ? httpDetailsParsedData[httpCurrentPage]?.user_agent
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <Link
                      color="text.link"
                      data-testid="see-more-button"
                      onClick={() => {
                        handleSearchQuery('http', httpCurrentPage);
                        setLogscaleDetailTitle(http.title);
                        showMoreConnectionOnOpen();
                      }}
                    >
                      {seeMore}
                    </Link>
                  </DetailsGrid>
                </>
              )}
            </AccordionPanel>
          )}
        </Box>

        <Box w="100%">
          {!logDetailsCreateQueryJobLoading &&
            !logDetailsLoading &&
            !sslDetailsParsedData && <NoDataPanel title={ssl.title} />}
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            sslDetailsParsedData) && (
            <AccordionPanel title={ssl.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <DetailsGrid>
                  <DetailsGridRow
                    title="Subject"
                    data-testid="detections-subject"
                  >
                    <Text overflowWrap="anywhere">
                      {sslDetailsParsedData?.subject
                        ? sslDetailsParsedData?.subject
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={ssl.certChainFps}
                    data-testid="detections-cert-chain-fps"
                  >
                    <Text overflowWrap="anywhere">{sslCertChainFPS}</Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={ssl.serverName}
                    data-testid="detections-server-name"
                  >
                    <Text overflowWrap="anywhere">
                      {sslDetailsParsedData?.server_name
                        ? sslDetailsParsedData?.server_name
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={ssl.issuer}
                    data-testid="detections-issuer"
                  >
                    <Text overflowWrap="anywhere">
                      {sslDetailsParsedData?.issuer
                        ? sslDetailsParsedData?.issuer
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <DetailsGridRow
                    title={ssl.validationStatus}
                    data-testid="detections-validation-status"
                  >
                    <Text overflowWrap="anywhere">
                      {sslDetailsParsedData?.validation_status
                        ? sslDetailsParsedData?.validation_status
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <Link
                    color="text.link"
                    data-testid="see-more-button"
                    onClick={() => {
                      handleSearchQuery('ssl', 0);
                      setLogscaleDetailTitle(ssl.title);
                      showMoreConnectionOnOpen();
                    }}
                  >
                    {seeMore}
                  </Link>
                </DetailsGrid>
              )}
            </AccordionPanel>
          )}
        </Box>

        <Box w="100%">
          {!logDetailsCreateQueryJobLoading &&
            !logDetailsLoading &&
            fileDetailsParsedData.length === 0 && (
              <NoDataPanel title={file.title} />
            )}
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            fileDetailsParsedData.length > 0) && (
            <AccordionPanel title={file.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <>
                  <SimpleCaretPagination
                    currentPage={filesCurrentPage}
                    pageLength={fileDetailsParsedData.length}
                    setCurrentPage={setFilesCurrentPage}
                    containerProps={{ mb: 4 }}
                  />
                  <DetailsGrid>
                    <DetailsGridRow
                      title={file.mimeType}
                      data-testid="detections-mime-type"
                    >
                      <Text overflowWrap="anywhere">
                        {fileDetailsParsedData[filesCurrentPage]?.mime_type
                          ? fileDetailsParsedData[filesCurrentPage]?.mime_type
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={file.rxHosts}
                      data-testid="detections-rx-hosts"
                    >
                      <Text overflowWrap="anywhere">
                        {fileDetailsParsedData[filesCurrentPage]?.[
                          'rx_hosts[0]'
                        ]
                          ? fileDetailsParsedData[filesCurrentPage]?.[
                              'rx_hosts[0]'
                            ]
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={file.seenBytes}
                      data-testid="detections-seen-bytes"
                    >
                      <Text overflowWrap="anywhere">
                        {fileDetailsParsedData[filesCurrentPage]?.seen_bytes
                          ? fileDetailsParsedData[filesCurrentPage]?.seen_bytes
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title="Filename"
                      data-testid="detections-filename"
                    >
                      <Text overflowWrap="anywhere">
                        {fileDetailsParsedData[filesCurrentPage]?.extracted
                          ? fileDetailsParsedData[filesCurrentPage]?.extracted
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow title="SHA1" data-testid="detections-sha1">
                      <Text overflowWrap="anywhere">
                        {fileDetailsParsedData[filesCurrentPage]?.sha1
                          ? fileDetailsParsedData[filesCurrentPage]?.sha1
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <Link
                      color="text.link"
                      data-testid="see-more-button"
                      onClick={() => {
                        handleSearchQuery('files', filesCurrentPage);
                        setLogscaleDetailTitle(file.title);
                        showMoreConnectionOnOpen();
                      }}
                    >
                      {seeMore}
                    </Link>
                  </DetailsGrid>
                </>
              )}
            </AccordionPanel>
          )}
        </Box>

        <Box w="100%">
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            smbDetailsParsedData) && (
            <AccordionPanel title={smb.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <DetailsGrid>
                  <DetailsGridRow title={smb.path} data-testid="smb-path">
                    <Text overflowWrap="anywhere">
                      {smbDetailsParsedData?.path
                        ? smbDetailsParsedData.path
                        : '-'}
                    </Text>
                  </DetailsGridRow>
                  <Link
                    color="text.link"
                    data-testid="see-more-button"
                    onClick={() => {
                      handleSearchQuery('smb_mapping', 0);
                      setLogscaleDetailTitle(smb.title);
                      showMoreConnectionOnOpen();
                    }}
                  >
                    {seeMore}
                  </Link>
                </DetailsGrid>
              )}
            </AccordionPanel>
          )}
        </Box>

        <Box w="100%">
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            intelDetailsParsedData.length > 0) && (
            <AccordionPanel title={intel.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <>
                  <SimpleCaretPagination
                    currentPage={intelCurrentPage}
                    pageLength={intelDetailsParsedData.length}
                    setCurrentPage={setIntelCurrentPage}
                    containerProps={{ mb: 4 }}
                  />
                  <DetailsGrid>
                    <DetailsGridRow
                      title={intel.indicator}
                      data-testid="intel-indicator"
                    >
                      <Text overflowWrap="anywhere">
                        {intelDetailsParsedData[intelCurrentPage]?.[
                          'seen.indicator'
                        ]
                          ? intelDetailsParsedData[intelCurrentPage][
                              'seen.indicator'
                            ]
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={intel.indicatorType}
                      data-testid="intel-indicator-type"
                    >
                      <Text overflowWrap="anywhere">
                        {intelDetailsParsedData[intelCurrentPage]?.[
                          'seen.indicator_type'
                        ]
                          ? intelDetailsParsedData[intelCurrentPage][
                              'seen.indicator_type'
                            ]
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={intel.where}
                      data-testid="intel-where"
                    >
                      <Text overflowWrap="anywhere">
                        {intelDetailsParsedData[intelCurrentPage]?.[
                          'seen.where'
                        ]
                          ? intelDetailsParsedData[intelCurrentPage][
                              'seen.where'
                            ]
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <Link
                      color="text.link"
                      data-testid="see-more-button"
                      onClick={() => {
                        handleSearchQuery('intel', intelCurrentPage);
                        setLogscaleDetailTitle(intel.title);
                        showMoreConnectionOnOpen();
                      }}
                    >
                      {seeMore}
                    </Link>
                  </DetailsGrid>
                </>
              )}
            </AccordionPanel>
          )}
        </Box>

        <Box w="100%">
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            noticeDetailsParsedData.length > 0) && (
            <AccordionPanel title={notice.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <>
                  <SimpleCaretPagination
                    currentPage={noticeCurrentPage}
                    pageLength={noticeDetailsParsedData.length}
                    setCurrentPage={setNoticeCurrentPage}
                    containerProps={{ mb: 4 }}
                  />
                  <DetailsGrid>
                    <DetailsGridRow title={notice.msg} data-testid="notice-msg">
                      <Text overflowWrap="anywhere">
                        {noticeDetailsParsedData[noticeCurrentPage]?.msg
                          ? noticeDetailsParsedData[noticeCurrentPage].msg
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow title={notice.sub} data-testid="notice-sub">
                      <Text overflowWrap="anywhere">
                        {noticeDetailsParsedData[noticeCurrentPage]?.sub
                          ? noticeDetailsParsedData[noticeCurrentPage].sub
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <DetailsGridRow
                      title={notice.note}
                      data-testid="notice-note"
                    >
                      <Text overflowWrap="anywhere">
                        {noticeDetailsParsedData[noticeCurrentPage]?.note
                          ? noticeDetailsParsedData[noticeCurrentPage].note
                          : '-'}
                      </Text>
                    </DetailsGridRow>
                    <Link
                      color="text.link"
                      data-testid="see-more-button"
                      onClick={() => {
                        handleSearchQuery('notice', noticeCurrentPage);
                        setLogscaleDetailTitle(notice.title);
                        showMoreConnectionOnOpen();
                      }}
                    >
                      {seeMore}
                    </Link>
                  </DetailsGrid>
                </>
              )}
            </AccordionPanel>
          )}
        </Box>

        {/* Yara Alerts Panel */}
        {alertType === 'yara_corelight' && (
          <Box w="100%">
            {!logDetailsCreateQueryJobLoading &&
              !logDetailsLoading &&
              yaraDetailsParsedData.length === 0 && (
                <NoDataPanel title={yara.title} />
              )}
            {(logDetailsCreateQueryJobLoading ||
              logDetailsLoading ||
              yaraDetailsParsedData.length > 0) && (
              <AccordionPanel title={yara.title} reduceMotion>
                {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                  <GraphQLReqStatus
                    loading={
                      logDetailsCreateQueryJobLoading || logDetailsLoading
                    }
                  />
                ) : (
                  <>
                    <SimpleCaretPagination
                      currentPage={yaraCurrentPage}
                      pageLength={yaraDetailsParsedData.length}
                      setCurrentPage={setYaraCurrentPage}
                      containerProps={{ mb: 4 }}
                    />
                    <DetailsGrid>
                      <DetailsGridRow
                        title={'Alert Signature'}
                        data-testid="yara-alert-signature"
                      >
                        <Text overflowWrap="anywhere">
                          {yaraDetailsParsedData[yaraCurrentPage]?.[
                            'alert.signature'
                          ] || '-'}
                        </Text>
                      </DetailsGridRow>
                    </DetailsGrid>
                    <Link
                      color="text.link"
                      data-testid="see-more-button"
                      onClick={() => {
                        handleSearchQuery('yara_corelight', yaraCurrentPage);
                        setLogscaleDetailTitle(yara.title);
                        showMoreConnectionOnOpen();
                      }}
                    >
                      {seeMore}
                    </Link>
                  </>
                )}
              </AccordionPanel>
            )}
          </Box>
        )}

        {/* Anomaly Alerts Panel */}
        {alertType === 'anomaly' && (
          <Box w="100%">
            {!logDetailsCreateQueryJobLoading &&
              !logDetailsLoading &&
              anomalyDetailsParsedData.length === 0 && (
                <NoDataPanel title={anomaly.title} />
              )}
            {(logDetailsCreateQueryJobLoading ||
              logDetailsLoading ||
              anomalyDetailsParsedData.length > 0) && (
              <AccordionPanel title={anomaly.title} reduceMotion>
                {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                  <GraphQLReqStatus
                    loading={
                      logDetailsCreateQueryJobLoading || logDetailsLoading
                    }
                  />
                ) : (
                  <>
                    <SimpleCaretPagination
                      currentPage={anomalyCurrentPage}
                      pageLength={anomalyDetailsParsedData.length}
                      setCurrentPage={setAnomalyCurrentPage}
                      containerProps={{ mb: 4 }}
                    />
                    <DetailsGrid>
                      <DetailsGridRow
                        title={anomaly.anomalyDescription}
                        data-testid="anomaly-alert-usecase-description"
                      >
                        <Text overflowWrap="anywhere">
                          {anomalyDetailsParsedData[anomalyCurrentPage]
                            ?.usecase_description || '-'}
                        </Text>
                      </DetailsGridRow>
                      <DetailsGridRow
                        title={anomaly.anomalousEntity}
                        data-testid="anomaly-alert-original-entity"
                      >
                        <Text overflowWrap="anywhere">
                          {anomalyDetailsParsedData[anomalyCurrentPage]
                            ?.original_entity || '-'}
                        </Text>
                      </DetailsGridRow>
                      <DetailsGridRow
                        title={anomaly.anomalousItem}
                        data-testid="anomaly-alert--item"
                      >
                        <Text overflowWrap="anywhere">
                          {anomalyDetailsParsedData[anomalyCurrentPage]?.item ||
                            '-'}
                        </Text>
                      </DetailsGridRow>
                      <DetailsGridRow
                        title={anomaly.anomalousItemScore}
                        data-testid="anomaly-alert-item-score"
                      >
                        <Text overflowWrap="anywhere">
                          {anomalyDetailsParsedData[anomalyCurrentPage]
                            ?.item_score || '-'}
                        </Text>
                      </DetailsGridRow>
                    </DetailsGrid>
                    <Link
                      color="text.link"
                      data-testid="see-more-button"
                      onClick={() => {
                        handleSearchQuery('anomaly', anomalyCurrentPage);
                        setLogscaleDetailTitle(anomaly.title);
                        showMoreConnectionOnOpen();
                      }}
                    >
                      {seeMore}
                    </Link>
                  </>
                )}
              </AccordionPanel>
            )}
          </Box>
        )}

        <Box w="100%">
          {(logDetailsCreateQueryJobLoading ||
            logDetailsLoading ||
            suricataDetailsParsedData.length > 0) && (
            <AccordionPanel title={suricata.title} reduceMotion>
              {logDetailsCreateQueryJobLoading || logDetailsLoading ? (
                <GraphQLReqStatus
                  loading={logDetailsCreateQueryJobLoading || logDetailsLoading}
                />
              ) : (
                <>
                  <SimpleCaretPagination
                    currentPage={suricataCurrentPage}
                    pageLength={suricataDetailsParsedData.length}
                    setCurrentPage={setSuricataCurrentPage}
                    containerProps={{ mb: 4 }}
                  />
                  <DetailsGrid>
                    <DetailsGridRow
                      title={suricata.category}
                      data-testid="suricata-category"
                    >
                      <Text overflowWrap="anywhere">
                        {suricataDetailsParsedData[suricataCurrentPage]?.[
                          'alert.category'
                        ] ?? '-'}
                      </Text>
                    </DetailsGridRow>
                  </DetailsGrid>
                  <Link
                    color="text.link"
                    data-testid="see-more-button"
                    onClick={() => {
                      handleSearchQuery(
                        'suricata_corelight',
                        suricataCurrentPage,
                      );
                      setLogscaleDetailTitle(suricata.title);
                      showMoreConnectionOnOpen();
                    }}
                  >
                    {seeMore}
                  </Link>
                </>
              )}
            </AccordionPanel>
          )}
        </Box>
      </Accordion>

      <LogscaleDetailsModal
        alertsName={alertsName}
        alertsTimestamp={alertsTimestamp}
        isModalOpen={showMoreConnectionIsOpen}
        onModalClose={showMoreConnectionOnClose}
        data={moreDetailsParsedData}
        title={logscaleDetailTitle || ''}
      />
      <InvestigateLogsTableModal
        drawerLoading={logDetailsLoading}
        isOpen={investigateModalIsOpen}
        onClose={investigateModalOnClose}
        onOpen={investigateModalOnOpen}
        alertsName={alertsName}
        alertsUid={alertsUid}
        alertsTimestamp={alertsTimestamp}
        alertsTenant={alertsTenant}
        investigateLogsTableData={
          logDetailsParsedData as unknown as LogDetails[]
        }
      />
    </VStack>
  );
};
